import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { formatMilliseconds } from '../../../utils/format';
import clsx from 'clsx';
import { HourglassIcon, SirenIcon } from 'lucide-react';
import { getColorForString } from '../../flow-color';

interface FlowBlockNodeData extends Record<string, unknown> {
  label: string;
  category: string;
  duration: number;
  moduleType: string;
  trackIndex: number;
  identifier: string;
  parameters: Record<string, unknown>;
  result: string;
  timestamp: number;
  timestampDisplay: string;
  contactFlowName: string;
  executionCount?: number;
}

type FlowBlockNode = Node<FlowBlockNodeData>;

export const FlowBlockNode = ({ data }: NodeProps<FlowBlockNode>) => {
  const colorScheme = getColorForString(data.contactFlowName);

  const isResultAnError = ['exception', 'invalid', 'timeout', 'error'].some((keyword) =>
    data.result?.toLowerCase().includes(keyword)
  );

  const isKeyValueAssignment = data.moduleType === 'SetAttributes';

  return (
    <>
      <Handle
        type='target'
        position={Position.Left}
        className='w-3 h-3 bg-neutral-400 border-2 border-white relative'
      />
      <div
        className={clsx(
          'bg-white border-2 rounded-lg overflow-hidden w-80',
          'hover:bg-slate-50! transition-all duration-75'
        )}
        style={{
          borderColor: colorScheme.borderColor
        }}
      >
        <div
          className='flex flex-col justify-between p-2 px-3 border-b-2 gap-1'
          style={{
            backgroundColor: colorScheme.bgColor,
            color: colorScheme.textColor,
            borderColor: colorScheme.borderColor
          }}
        >
          <div className='text-xs'>{data.contactFlowName}</div>
          <div className='flex justify-between items-center gap-2'>
            <div className='text-md font-semibold truncate'>{data.label}</div>
            {data.duration !== undefined && (
              <div
                className={clsx(
                  'font-mono text-sm tabular-nums flex flex-row gap-1 items-center',
                  'bg-white/90 px-2 py-0.5 rounded-full',
                  {
                    'text-red-900': data.duration > 500 && data.moduleType !== 'GetUserInput',
                    'text-inherit': data.duration <= 500
                  }
                )}
              >
                <HourglassIcon className='size-3' />
                {formatMilliseconds(data.duration)}
                {data.duration > 500 && <SirenIcon className='size-3.5' />}
              </div>
            )}
          </div>
          <div className='flex justify-between items-center'>
            <div className='text-xs tabular-nums'>{data.identifier}</div>
            {data.executionCount !== undefined && data.executionCount > 0 && (
              <div className={clsx('text-xs font-mono px-2 py-0.5 rounded-full', 'bg-white/90')}>
                #{data.executionCount + 1}
              </div>
            )}
          </div>
        </div>
        <div
          className='absolute h-2 w-full -top-12 -left-12'
          style={{
            backgroundColor: colorScheme.borderColor,
            width: 'calc(100% + 100px)'
          }}
        />
        {/* <div
          className='absolute w-full -left-12 -top-10 py-50! h-full rounded-xl  opacity-50 -z-10'
          style={{
            backgroundColor: colorScheme.bgColor,
            width: 'calc(100% + 100px)'
          }}
        /> */}
        <div className='flex flex-col gap-2 px-3 py-3'>
          <div className='text-xs text-neutral-700'>{data.timestampDisplay}</div>

          {data.parameters &&
            !isKeyValueAssignment &&
            Object.entries(data.parameters).map(([key, value]) => (
              <div key={key} className='text-sm flex flex-col gap-0.5'>
                <span className='font-semibold text-neutral-950'>{key}</span>
                <div className='text-neutral-800 break-words text-sm'>
                  {typeof value === 'object' ? (
                    <pre className='wrap-normal text-wrap'>{JSON.stringify(value, null, 2)}</pre>
                  ) : (
                    String(value) || '<none>'
                  )}
                </div>
              </div>
            ))}
          {data.parameters &&
            isKeyValueAssignment &&
            Object.entries(data.parameters).map(([key, value]) => (
              <div key={key} className='text-sm flex flex-col gap-0.5'>
                <div className='text-neutral-800 break-words text-sm'>
                  {typeof value === 'object' ? (
                    <pre className='wrap-normal text-wrap'>{JSON.stringify(value, null, 2)}</pre>
                  ) : (
                    String(value) || '<none>'
                  )}
                </div>
              </div>
            ))}

          {data.result && (
            <div>
              <hr className='py-1' />
              <span className='text-sm font-semibold text-neutral-950'>Result</span>
              <div className={clsx('break-words text-sm', isResultAnError ? 'text-red-900' : 'text-primary')}>
                {data.result}
              </div>
            </div>
          )}
        </div>
      </div>
      <Handle type='source' position={Position.Right} className='w-3 h-3 bg-neutral-400 border-2 border-white' />
    </>
  );
};
